import React, { useState, useEffect } from 'react';
import {
  Settings, Calendar, Plus, CheckCircle2, Clock,
  Loader2, AlertCircle, ChevronRight, Zap, Archive,
  Building2, Bell, X,
} from 'lucide-react';
import { useParentAppSchool } from '../ParentAppContext';
import {
  AcademicYear,
  getAcademicYears,
  createAcademicYear,
  getTransition,
  AcademicYearTransition,
} from '@/actions/data-fetch-academic-years';
import AcademicYearWizard from './AcademicYearWizard';

// ─── Notification types ───────────────────────────────────────────────────────
const NOTIF_TYPES = [
  { id: 'announcement',  label: 'Announcement',     cat: 'cat-blue'   },
  { id: 'event',         label: 'School Event',     cat: 'cat-purple' },
  { id: 'exam',          label: 'Exam / Test',      cat: 'cat-rose'   },
  { id: 'holiday',       label: 'Holiday Notice',   cat: 'cat-teal'   },
  { id: 'attendance',    label: 'Attendance Alert', cat: 'cat-amber'  },
  { id: 'homework',      label: 'Homework',         cat: 'cat-green'  },
  { id: 'fee_updated',   label: 'Fee Reminder',     cat: 'cat-indigo' },
  { id: 'new_document',  label: 'New Document',     cat: 'cat-peach'  },
  { id: 'call_reminder', label: 'Call Reminder',    cat: 'cat-cyan'   },
  { id: 'new_post',      label: 'New Post',         cat: 'cat-blue'   },
];

// ─── Settings card wrapper ────────────────────────────────────────────────────
const SettingsCard: React.FC<{
  icon: React.ReactNode;
  cat: string;
  title: string;
  sub: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, cat, title, sub, action, children }) => (
  <div className="card" style={{ marginBottom: 18 }}>
    <div className="row gap-12" style={{ padding: '18px 20px', borderBottom: '1px solid var(--divider)' }}>
      <div className={`center ${cat}`} style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.3px', color: 'var(--text)' }}>{title}</div>
        <div className="meta" style={{ marginTop: 1 }}>{sub}</div>
      </div>
      {action}
    </div>
    <div style={{ padding: '18px 20px' }}>{children}</div>
  </div>
);

// ─── Create Year Modal ────────────────────────────────────────────────────────
const CreateYearModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    year_label: `${currentYear}-${String(currentYear + 1).slice(-2)}`,
    start_date: `${currentYear}-04-01`,
    end_date:   `${currentYear + 1}-03-31`,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await createAcademicYear(form);
      onCreated();
    } catch (err: any) { setError(err.message); setSaving(false); }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal">
        <div className="modal-head" style={{ borderBottom: '1px solid var(--divider)' }}>
          <div style={{ flex: 1 }}>
            <h2>New Academic Year</h2>
            <p>Set the label and date range for the new year</p>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <form id="create-year-form" onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="col gap-14" style={{ paddingTop: 8 }}>
              <div className="field">
                <label>Year label <span className="req">*</span></label>
                <input
                  autoFocus className="input" value={form.year_label}
                  onChange={e => setForm(f => ({ ...f, year_label: e.target.value }))}
                  placeholder="e.g. 2026-27" required
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="field">
                  <label>Start date <span className="req">*</span></label>
                  <input type="date" className="input" value={form.start_date}
                    onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} required />
                </div>
                <div className="field">
                  <label>End date <span className="req">*</span></label>
                  <input type="date" className="input" value={form.end_date}
                    onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} required />
                </div>
              </div>
              {error && (
                <div className="row gap-8" style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--danger-wash)', color: 'var(--danger)', fontSize: 13 }}>
                  <AlertCircle size={15} style={{ flexShrink: 0 }} />
                  {error}
                </div>
              )}
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button form="create-year-form" type="submit" className="btn btn-primary" disabled={saving}>
              {saving && <Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} />}
              {saving ? 'Creating…' : 'Create year'}
            </button>
          </div>
        </form>
      </div>
      <style>{`@keyframes pa-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const PortalSettings: React.FC = () => {
  const { school, refreshStatus } = useParentAppSchool();

  // Academic years state
  const [years,       setYears]       = useState<AcademicYear[]>([]);
  const [transitions, setTransitions] = useState<Record<number, AcademicYearTransition>>({});
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [wizardYear,  setWizardYear]  = useState<AcademicYear | null>(null);

  // Notification prefs state (UI only)
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(NOTIF_TYPES.map(t => [t.id, true]))
  );

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { years: ys = [] } = await getAcademicYears();
      const safeYears = Array.isArray(ys) ? ys : [];
      setYears(safeYears);
      if (safeYears.length > 0) {
        const results = await Promise.allSettled(safeYears.map(y => getTransition(y.id)));
        const map: Record<number, AcademicYearTransition> = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value) map[safeYears[i].id] = r.value;
        });
        setTransitions(map);
      }
    } catch (e) { console.error('Failed to load academic years:', e); }
    finally { setLoading(false); }
  };

  const handleCreated = async () => {
    setShowCreate(false);
    await fetchData();
  };

  const handleActivated = async () => {
    setWizardYear(null);
    await fetchData();
    await refreshStatus();
  };

  const activeYearId = school?.activeAcademicYearId;

  const getYearStatus = (year: AcademicYear) => {
    if (activeYearId === year.id) return 'active';
    const t = transitions[year.id];
    if (!t) return 'available';
    return t.status;
  };

  const sortedYears = (Array.isArray(years) ? [...years] : []).sort((a, b) =>
    new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  );

  const YEAR_STATUS_BADGE: Record<string, { cls: string; label: string }> = {
    active:            { cls: 'b-success', label: 'Active' },
    completed:         { cls: 'b-success', label: 'Completed' },
    promotions_mapped: { cls: 'b-info',    label: 'Promotions done' },
    classes_generated: { cls: 'b-info',    label: 'Classes ready' },
    draft:             { cls: 'b-warning', label: 'In progress' },
    available:         { cls: 'b-neutral', label: 'Not started' },
  };

  return (
    <>
      <style>{`@keyframes pa-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Page header */}
      <div className="page-head">
        <div className="page-head-icon">
          <Settings size={20} strokeWidth={2} />
        </div>
        <div className="page-head-text">
          <h1>Settings</h1>
          <p>School-level configuration</p>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div style={{ maxWidth: 720 }}>

          {/* ── School Profile ── */}
          <SettingsCard icon={<Building2 size={20} />} cat="cat-blue" title="School Profile" sub="Name, logo and contact details">
            <div className="row gap-16" style={{ marginBottom: 18 }}>
              <div className="center" style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--slate)', color: 'var(--accent)', flexShrink: 0 }}>
                <Building2 size={26} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>School logo</div>
                <div className="meta" style={{ margin: '2px 0 8px' }}>Shown in the parent app</div>
                <button className="btn btn-soft btn-sm">Replace</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="field">
                <label>School name</label>
                <input className="input" defaultValue={school?.school_name || ''} placeholder="School name" readOnly style={{ cursor: 'default' }} />
              </div>
              <div className="field">
                <label>Board affiliation</label>
                <input className="input" placeholder="e.g. CBSE" />
              </div>
              <div className="field">
                <label>Contact email</label>
                <input className="input" type="email" placeholder="office@school.edu" />
              </div>
              <div className="field">
                <label>Contact phone</label>
                <input className="input" type="tel" placeholder="+91 98765 43210" />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Address</label>
                <input className="input" placeholder="School address" />
              </div>
            </div>
          </SettingsCard>

          {/* ── Academic Year ── */}
          <SettingsCard
            icon={<Calendar size={20} />}
            cat="cat-purple"
            title="Academic Years"
            sub="Manage year lifecycle and student promotions"
            action={
              <button className="btn btn-soft btn-sm" onClick={() => setShowCreate(true)}>
                <Plus size={14} />
                New year
              </button>
            }
          >
            {loading ? (
              <div className="center" style={{ padding: '24px 0' }}>
                <Loader2 size={20} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
              </div>
            ) : years.length === 0 ? (
              <div className="empty" style={{ padding: '24px 16px' }}>
                <div className="empty-art" style={{ width: 56, height: 56, borderRadius: 16, marginBottom: 12 }}>
                  <Calendar size={24} />
                </div>
                <h3 style={{ fontSize: 15 }}>No academic years yet</h3>
                <p>Create your first academic year to get started</p>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                  <Plus size={14} /> Create academic year
                </button>
              </div>
            ) : (
              <div className="col" style={{ gap: 0 }}>
                {sortedYears.map((year, idx) => {
                  const status   = getYearStatus(year);
                  const trans    = transitions[year.id];
                  const isActive = status === 'active';
                  const isDone   = trans?.status === 'completed';
                  const badge    = YEAR_STATUS_BADGE[status] ?? YEAR_STATUS_BADGE.available;

                  return (
                    <div key={year.id} className="row gap-12" style={{
                      padding: '12px 0',
                      borderBottom: idx < sortedYears.length - 1 ? '1px solid var(--divider)' : 'none',
                    }}>
                      {/* Icon */}
                      <div className={`center ${isActive ? 'cat-green' : 'cat-blue'}`}
                        style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0 }}>
                        {isActive
                          ? <Zap size={16} />
                          : isDone
                            ? <Archive size={16} />
                            : <Clock size={16} />}
                      </div>

                      {/* Label + dates */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="row gap-8" style={{ marginBottom: 2 }}>
                          <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{year.year_label}</span>
                          <span className={`badge ${badge.cls}`}>{badge.label}</span>
                        </div>
                        <div className="meta">
                          {new Date(year.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' – '}
                          {new Date(year.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                        {trans && !isActive && (trans.classes_generated > 0 || trans.students_promoted > 0) && (
                          <div className="row gap-8 meta" style={{ marginTop: 2 }}>
                            {trans.classes_generated > 0 && <span>{trans.classes_generated} classes</span>}
                            {trans.students_promoted > 0 && <span>{trans.students_promoted} promoted</span>}
                          </div>
                        )}
                      </div>

                      {/* Action */}
                      <div style={{ flexShrink: 0 }}>
                        {isActive ? (
                          <div className="row gap-6" style={{ color: 'var(--success)', fontSize: 12.5, fontWeight: 700 }}>
                            <CheckCircle2 size={14} /> Current year
                          </div>
                        ) : isDone ? (
                          <span className="meta">Archived</span>
                        ) : (
                          <button className="btn btn-soft btn-sm" onClick={() => setWizardYear(year)}>
                            {!trans || trans.status === 'draft' ? 'Set up' : 'Continue'}
                            <ChevronRight size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SettingsCard>

          {/* ── Notification preferences ── */}
          <SettingsCard icon={<Bell size={20} />} cat="cat-amber" title="Notification Preferences" sub="Which events trigger a push notification to parents">
            <div className="col">
              {NOTIF_TYPES.map((n, i) => (
                <div key={n.id} className="row" style={{
                  padding: '10px 0',
                  borderBottom: i < NOTIF_TYPES.length - 1 ? '1px solid var(--divider)' : 'none',
                }}>
                  <span className={`chip chip-sm ${n.cat}`}>{n.label}</span>
                  <div className="spacer" />
                  <button
                    className={`toggle${notifPrefs[n.id] ? ' on' : ''}`}
                    onClick={() => setNotifPrefs(p => ({ ...p, [n.id]: !p[n.id] }))}
                  />
                </div>
              ))}
            </div>
          </SettingsCard>

        </div>
      </div>

      {showCreate && <CreateYearModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      {wizardYear && (
        <AcademicYearWizard
          year={wizardYear}
          onClose={() => setWizardYear(null)}
          onActivated={handleActivated}
        />
      )}
    </>
  );
};

export default PortalSettings;
